import { STORE_DEV_PRODUCT_PREFIX } from './catalog';
import type { StoreOrderState } from './orders';
import {
  parseReleaseStage,
  type ReleaseStage,
  type ReleaseTransition,
  stagesFunding,
} from './release';

/**
 * What an account is given back at each reset, and what that is computed from.
 *
 * ## Two resets, two multipliers, one base
 *
 *     closed alpha -> open alpha :  100%   of what was bought in the closed alpha
 *     alphas       -> official   :  130%   of what was bought in BOTH alphas
 *
 * A purchase made in the closed alpha therefore funds both credits: it is
 * returned whole at the open alpha and returned again, with 30% on top, at
 * launch. That is not double counting - they are two different resets, each
 * crediting once, and each recorded once.
 *
 * ## It is NOT the wallet
 *
 * The right is earned by BUYING, not by holding. Spending the Cash in the
 * gacha, burning it as a market fee or sending it to somebody else changes the
 * balance and changes nothing here - and the person who receives it in a trade
 * earns no right at all, because they did not buy anything. A balance-based
 * rule would let one purchase launder into two entitlements, and would punish
 * exactly the players who used what they paid for.
 *
 * The Cash CREDITED by a reset is not a purchase either: it writes no
 * `cash_purchases` row, so it cannot appear in the next base however large the
 * balance it produced.
 *
 * ## The arithmetic
 *
 *     eligible = sum of every eligible purchase in the funding stages
 *     granted  = floor(eligible * multiplier / 10000)
 *
 * The SUM comes first and the floor once, at the end. Flooring purchase by
 * purchase and adding afterwards loses up to one Cash per receipt, which turns
 * an obligation into an arithmetic accident nobody can audit: 275 + 275 is 715,
 * not 357 + 357 = 714.
 *
 * ## The projection is a rule; the GRANT is a row
 *
 * Nothing here is materialised. A stored recredit balance would be a second
 * fact about the same purchases, and the two would disagree the first time a
 * refund landed after the balance was written. The preview is derived on every
 * read. What was actually PAID is recorded once per account per destination, in
 * `release_transition_grants`, and that row is what makes a second credit
 * impossible.
 */

/** 130% - the 100% purchased plus the 30% bonus. Integer basis points. */
export const LAUNCH_RECREDIT_BASIS_POINTS = 13_000;

/** 100%. The closed alpha's purchases come back whole at the open alpha. */
export const OPEN_ALPHA_RESTORE_BASIS_POINTS = 10_000;

/** The bonus alone, as the interface states it. 130% - 100%. */
export const RECREDIT_BONUS_PERCENT = 30;

/** The multiplier a destination pays. Integer basis points, like every rate. */
export function multiplierForTransition(destination: ReleaseTransition): number {
  return destination === 'open_alpha'
    ? OPEN_ALPHA_RESTORE_BASIS_POINTS
    : LAUNCH_RECREDIT_BASIS_POINTS;
}

/**
 * `floor(base * multiplier / 10000)`, in `bigint`.
 *
 * Floors, so a fractional unit of Cash never appears: 275 at 130% gives 357,
 * not 357.5. The loss is at most one Cash and always in the player's disfavour
 * by less than a unit - the alternative, rounding up, would mint Cash nobody
 * paid for. Called ONCE, on the total. See the module note.
 */
export function applyRecreditMultiplier(base: bigint, basisPoints: number): bigint {
  if (base <= 0n || basisPoints <= 0) {
    return 0n;
  }
  return (base * BigInt(basisPoints)) / 10_000n;
}

/** The official launch's 130%, as its own name. */
export function launchRecreditFor(purchasedCash: bigint): bigint {
  return applyRecreditMultiplier(purchasedCash, LAUNCH_RECREDIT_BASIS_POINTS);
}

/**
 * Does a purchase of this product count towards the right?
 *
 * Only real commercial products do. The sandbox validation product exists to
 * drive a test charge and delivers one Cash; letting it into the base would
 * turn an internal fixture into a launch obligation.
 *
 * The check is on the PRODUCT CODE stored with the purchase - `id@version` -
 * because that is what a purchase row keeps, and because the catalogue it came
 * from may have changed since.
 */
export function purchaseCountsTowardsRecredit(productCode: string): boolean {
  return !productCode.startsWith(STORE_DEV_PRODUCT_PREFIX);
}

/* -------------------------------------------------------------------------- */
/*  Eligibility                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Why a purchase is not in the base.
 *
 * A closed list, because the interface groups by it and a support conversation
 * quotes it. Every reason is safe to show: it names a CATEGORY, never a payment,
 * a provider id, a reference or an amount somebody else could correlate.
 */
export const RECREDIT_EXCLUSION_REASONS = [
  /** Refunded, charged back or successfully contested. Any positive amount. */
  'refunded',
  /** Cash that never came from a store order: a grant, a reward, a fixture. */
  'not_a_store_purchase',
  /** A store order exists but does not prove what was sold, or two claim it. */
  'unproven',
  /** Paid, perhaps, but the benefit was never delivered. */
  'not_granted',
  /** Paid with play money. A sandbox charge is not a purchase. */
  'sandbox',
  /** The internal Pix validation product. An instrument, not an offer. */
  'validation_product',
  /** VIP, or anything else that is not Cash. */
  'not_a_cash_product',
  /**
   * The order does not say which stage it was sold in.
   *
   * Every order created from now on freezes one. A row from before the stage
   * existed cannot be assigned to an alpha after the fact - guessing would put
   * somebody's old purchase into a base it may not belong to - so it is stated
   * as unknown and stays out.
   */
  'unknown_stage',
] as const;
export type RecreditExclusionReason = (typeof RECREDIT_EXCLUSION_REASONS)[number];

/**
 * The same reasons, in the language they are read in.
 *
 * Here rather than in a component, for the same reason the disclaimers are: the
 * category IS the explanation a player is owed, and a screen that reworded it
 * would eventually explain a refusal the server never made. Nothing here names
 * a payment, an order or an amount - a reason applies to a COUNT of purchases,
 * never to an identifiable one.
 */
export const RECREDIT_EXCLUSION_LABELS: Readonly<Record<RecreditExclusionReason, string>> =
  Object.freeze({
    refunded: 'Compras reembolsadas, estornadas ou contestadas',
    not_a_store_purchase: 'Cash recebido fora da loja (trade, mercado, recompensa ou concessão)',
    unproven: 'Compras sem comprovação suficiente',
    not_granted: 'Compras cujo benefício ainda não foi entregue',
    sandbox: 'Pagamentos de teste (sandbox)',
    validation_product: 'Produtos internos de validação',
    not_a_cash_product: 'Compras que não entregam Cash, como VIP',
    unknown_stage: 'Compras sem fase de lançamento registrada',
  });

/** Order states in which money has gone back, or is going back. */
const REFUND_STATES: readonly StoreOrderState[] = Object.freeze([
  'refund_pending',
  'refunded',
] as const);

/**
 * A refund the provider has confirmed.
 *
 * `amountCents` is `null` when the provider did not say how much - which is the
 * normal case for the Orders resource, and is treated as a refund, not as an
 * absence of one. Only a refund recorded as exactly zero is no refund at all.
 */
export interface RecreditRefundFact {
  readonly amountCents: number | null;
}

/**
 * The store order that proves a purchase, read from its FROZEN snapshot.
 *
 * Never from today's catalogue: a product whose Cash amount changed after
 * somebody bought it must not retroactively change what they are owed.
 */
export interface RecreditOrderFact {
  readonly state: StoreOrderState;
  /** 'production' or 'sandbox'. Only the first is real money. */
  readonly providerEnvironment: string;
  readonly productId: string;
  /** The snapshot's `kind`. 'cash' or 'vip'. */
  readonly productKind: string;
  /** The snapshot's Cash amount. Must agree with the purchase row. */
  readonly snapshotCashAmount: bigint;
  /**
   * The release stage FROZEN on the order when the server created it.
   *
   * Which reset a purchase funds depends entirely on when it was made, so this
   * is written by the server at creation and never sent by a client. Anything
   * unrecognised - including the empty string a row from before the field
   * carries - excludes the purchase rather than being assigned to a stage.
   */
  readonly stage: string;
}

/**
 * One `cash_purchases` row and everything that can be proved about it.
 *
 * `orders` is every store order naming this purchase. Zero means the Cash did
 * not come from the store at all; more than one means the link is ambiguous,
 * and an ambiguous proof is not a proof.
 */
export interface RecreditPurchaseFact {
  readonly purchaseId: string;
  /** `productId@version`, as the purchase row stores it. */
  readonly productCode: string;
  readonly cashAmount: bigint;
  readonly refund: RecreditRefundFact | null;
  readonly orders: readonly RecreditOrderFact[];
}

export interface RecreditVerdict {
  readonly purchaseId: string;
  readonly eligible: boolean;
  /** What it contributes. Zero for anything excluded. */
  readonly cash: bigint;
  readonly reason: RecreditExclusionReason | null;
  /** The stage it was bought in, when that could be established. */
  readonly stage: ReleaseStage | null;
}

/**
 * Is this purchase part of the launch recredit?
 *
 * Every condition is a REFUSAL that has to be argued away, not a permission
 * that has to be found. A purchase whose origin, environment, product or
 * benefit cannot be established from the rows themselves is excluded as
 * `unproven` - a missing field is never filled in by assumption, because the
 * only way to guess wrong here is in the direction of paying for something
 * nobody bought.
 *
 * The refund check comes FIRST, and it is total. There is no proportional
 * recredit: the store delivers a product whole and takes it back whole, so a
 * partly-returned payment leaves the base entirely. Doing otherwise would owe
 * somebody 30% of Cash they were reimbursed for.
 */
export function recreditVerdict(fact: RecreditPurchaseFact): RecreditVerdict {
  const stage = parseReleaseStage(fact.orders[0]?.stage);
  const excluded = (reason: RecreditExclusionReason): RecreditVerdict => ({
    purchaseId: fact.purchaseId,
    eligible: false,
    cash: 0n,
    reason,
    stage,
  });

  // Any positive refund invalidates the whole purchase. An unknown amount is a
  // refund - the Orders resource does not report one, and "we could not tell"
  // must not resolve in the payer's favour twice.
  if (fact.refund !== null && (fact.refund.amountCents === null || fact.refund.amountCents > 0)) {
    return excluded('refunded');
  }
  if (fact.orders.length === 0) {
    return excluded('not_a_store_purchase');
  }
  if (fact.orders.length > 1) {
    return excluded('unproven');
  }
  const order = fact.orders[0] as RecreditOrderFact;

  if (REFUND_STATES.includes(order.state)) {
    return excluded('refunded');
  }
  if (order.state !== 'granted') {
    // Approved is not enough: the account has to have RECEIVED the Cash.
    return excluded('not_granted');
  }
  if (order.providerEnvironment !== 'production') {
    return excluded('sandbox');
  }
  /*
   * The development prefix is checked on BOTH the purchase's product code and
   * the order's product id, so changing one of the two later cannot smuggle a
   * fixture in. The validation product is `dev_pix_validation_v1`; this
   * excludes it however its definition is edited afterwards.
   */
  if (
    !purchaseCountsTowardsRecredit(fact.productCode) ||
    order.productId.startsWith(STORE_DEV_PRODUCT_PREFIX)
  ) {
    return excluded('validation_product');
  }
  if (order.productKind !== 'cash' || order.snapshotCashAmount <= 0n) {
    return excluded('not_a_cash_product');
  }
  if (fact.cashAmount <= 0n) {
    return excluded('not_a_cash_product');
  }
  // The purchase and the order have to agree about what was delivered, and the
  // purchase has to belong to the order's product.
  if (fact.cashAmount !== order.snapshotCashAmount) {
    return excluded('unproven');
  }
  if (!fact.productCode.startsWith(`${order.productId}@`)) {
    return excluded('unproven');
  }
  if (stage === null) {
    // Last, so a purchase that fails anything else is reported by that reason
    // rather than by a missing field it also happens to have.
    return excluded('unknown_stage');
  }

  return {
    purchaseId: fact.purchaseId,
    eligible: true,
    cash: fact.cashAmount,
    reason: null,
    stage,
  };
}

/** How many purchases each exclusion reason accounts for. Never which ones. */
export interface RecreditExclusionCount {
  readonly reason: RecreditExclusionReason;
  readonly count: number;
}

/** What one reset would credit, and from which purchases. */
export interface RecreditProjection {
  readonly destination: ReleaseTransition;
  /** 10000 for the open alpha, 13000 for the official launch. */
  readonly multiplierBasisPoints: number;
  /** The stages whose purchases fund it. */
  readonly fundedBy: readonly ReleaseStage[];
  readonly eligiblePurchasedCash: bigint;
  readonly grantedCash: bigint;
  /** `grantedCash - eligiblePurchasedCash`. Zero at 100%. */
  readonly bonusCash: bigint;
  readonly eligiblePurchaseCount: number;
}

export interface RecreditSummary {
  /** Both resets, always in the same order: open alpha, then official. */
  readonly projections: readonly RecreditProjection[];
  /** Eligible purchases in any stage. */
  readonly eligiblePurchaseCount: number;
  readonly excludedPurchaseCount: number;
  /** Ascending by reason, so the answer is stable between two reads. */
  readonly exclusions: readonly RecreditExclusionCount[];
}

/**
 * What ONE reset would credit.
 *
 * Filters the eligible purchases down to the stages that fund this destination,
 * sums them in `bigint`, and applies the multiplier ONCE to the total. A
 * purchase made in the closed alpha appears in both projections - that is the
 * design, not double counting: the open alpha returns it whole, and the launch
 * returns it again with 30% on top, each exactly once.
 */
export function recreditProjection(
  facts: readonly RecreditPurchaseFact[],
  destination: ReleaseTransition,
): RecreditProjection {
  const funding = stagesFunding(destination);
  let eligiblePurchasedCash = 0n;
  let eligiblePurchaseCount = 0;

  for (const fact of facts) {
    const verdict = recreditVerdict(fact);
    if (!verdict.eligible || verdict.stage === null || !funding.includes(verdict.stage)) {
      continue;
    }
    eligiblePurchasedCash += verdict.cash;
    eligiblePurchaseCount += 1;
  }

  const multiplierBasisPoints = multiplierForTransition(destination);
  const grantedCash = applyRecreditMultiplier(eligiblePurchasedCash, multiplierBasisPoints);
  return {
    destination,
    multiplierBasisPoints,
    fundedBy: funding,
    eligiblePurchasedCash,
    grantedCash,
    bonusCash: grantedCash - eligiblePurchasedCash,
    eligiblePurchaseCount,
  };
}

/**
 * Both projections and the exclusions, for one account.
 *
 * The exclusions are stage-independent: they say why a purchase counts for
 * NOTHING, not why it counts for one reset and not the other. A closed-alpha
 * purchase is not "excluded" from the official base for being early - it is in
 * both.
 */
export function summariseRecredit(facts: readonly RecreditPurchaseFact[]): RecreditSummary {
  let eligiblePurchaseCount = 0;
  const counts = new Map<RecreditExclusionReason, number>();

  for (const fact of facts) {
    const verdict = recreditVerdict(fact);
    if (verdict.eligible) {
      eligiblePurchaseCount += 1;
      continue;
    }
    const reason = verdict.reason as RecreditExclusionReason;
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }

  return {
    projections: [recreditProjection(facts, 'open_alpha'), recreditProjection(facts, 'official')],
    eligiblePurchaseCount,
    excludedPurchaseCount: facts.length - eligiblePurchaseCount,
    exclusions: [...counts.entries()]
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => (a.reason < b.reason ? -1 : 1)),
  };
}

/* -------------------------------------------------------------------------- */
/*  What the player is told                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The preview is an ESTIMATE and says so in its own payload.
 *
 * A single literal rather than a boolean: when a paid registry exists, it will
 * answer something else here, and every reader will have had to handle a value
 * it did not recognise from the first day.
 */
export const RECREDIT_PREVIEW_STATUS = 'estimate' as const;

/**
 * The wording that travels with the estimate, in the language it is read in.
 *
 * Here rather than in the interface because it is part of the ANSWER: a number
 * called "1.300" beside a currency symbol somebody drew is a promise, and every
 * qualification that makes it not one has to be as authoritative as the number.
 * The interface renders these; it does not compose them.
 */
export const RECREDIT_DISCLAIMERS: readonly string[] = Object.freeze([
  'Esta é uma estimativa. Os créditos ainda não foram executados.',
  'O Cash devolvido em um reset não é uma nova compra e não entra novamente na base do recredito seguinte.',
  'Não é o seu saldo atual de Cash, e não muda o que você tem hoje.',
  'Não é saldo em reais, não é saque, não é rendimento e não é investimento.',
  'Somente Cash comprado diretamente na loja Kenocyber é elegível.',
  'Cash recebido por trade, mercado, recompensa, evento ou administrador não participa.',
  'O valor pode diminuir se uma compra for reembolsada, estornada ou contestada.',
  'Gastar o Cash no jogo não reduz a estimativa: o direito é de quem comprou.',
]);
